import React, { useState, useEffect } from "react";
import axios from "../../api/api";
import { toast } from "react-toastify";

export default function EventCategoryAdmin() {
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [editCategory, setEditCategory] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const resp = await axios.get("/api/event/category/categories");
      setCategories(resp.data.data || []);
    } catch (err) {
      console.error("Error fetching categories:", err);
      toast.error("Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.trim()) {
      toast.error("Category name cannot be empty");
      return;
    }
    try {
      const resp = await axios.post("/api/event/category/add-category", {
        name: newCategory.trim(),
      });
      setCategories([...categories, resp.data.data]);
      setNewCategory("");
      toast.success("Category added successfully!");
    } catch (err) {
      console.error("Error adding category:", err);
      toast.error(err.response?.data?.message || "Failed to add category");
    }
  };

  const handleSaveEdit = async (e, categoryId) => {
    e.preventDefault();
    if (!editCategory.name.trim()) {
      toast.error("Category name cannot be empty");
      return;
    }
    try {
      const resp = await axios.put(`/api/event/category/${categoryId}`, {
        name: editCategory.name.trim(),
      });
      setCategories(
        categories.map((cat) =>
          cat._id === categoryId ? resp.data.data : cat
        )
      );
      setEditCategory(null);
      toast.success("Category updated successfully!");
    } catch (err) {
      console.error("Error updating category:", err);
      toast.error(err.response?.data?.message || "Failed to update category");
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm("Are you sure? Events may be affected.")) return;

    console.log("Delete called for ID:", categoryId); // Frontend log

    try {
      const resp = await axios.delete(`/api/event/category/${categoryId}`);
      console.log("Delete response:", resp.data); // Log response
      setCategories(categories.filter((cat) => cat._id !== categoryId));
      toast.success("Category deleted successfully!");
    } catch (err) {
      console.error("Delete error:", err.response?.data || err); // Log full error
      toast.error(err.response?.data?.message || "Failed to delete category");
    }
  };

  const startEditing = (category) => {
    setEditCategory({ _id: category._id, name: category.name });
  };

  const cancelEditing = () => {
    setEditCategory(null);
  };

  return (
    <div className="flex flex-1 bg-gray-100 dark:bg-gray-900">
      <div className="page-wrapper relative ml-auto w-[calc(100%-260px)] px-6 pt-24 duration-300">
        <div className="xl:w-full min-h-[calc(100vh-152px)] relative pb-14">
          <div className="grid md:grid-cols-12 lg:grid-cols-12 xl:grid-cols-12 gap-6 mb-6">
            <div className="sm:col-span-12 md:col-span-12 lg:col-span-8 xl:col-span-6 xl:col-start-4">
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg w-full relative mb-6 shadow-md">
                <div className="border-b border-gray-200 dark:border-gray-700 py-4 px-6 dark:text-gray-300">
                  <h4 className="font-semibold text-xl flex-1 self-center mb-2 md:mb-0">
                    Manage Event Categories
                  </h4>
                </div>
                <div className="p-6">
                  {/* Add Category Form */}
                  <form onSubmit={handleAddCategory} className="mb-6">
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        New Category Name
                      </label>
                      <input
                        type="text"
                        value={newCategory}
                        onChange={(e) => setNewCategory(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
                        placeholder="Enter category name"
                      />
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="submit"
                        className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                        disabled={loading || !newCategory.trim()}
                      >
                        Add Category
                      </button>
                    </div>
                  </form>

                  {/* Category List */}
                  <div className="mt-8">
                    <h5 className="font-semibold text-base text-gray-700 dark:text-gray-300 mb-3">
                      Existing Categories
                    </h5>
                    {loading ? (
                      <div className="space-y-4">
                        {Array(3).fill().map((_, idx) => (
                          <div key={idx} className="animate-pulse bg-gray-200 dark:bg-gray-700 h-12 rounded-md"></div>
                        ))}
                      </div>
                    ) : categories.length === 0 ? (
                      <p className="text-gray-600 dark:text-gray-400 text-sm">
                        No categories found. Add a new category above.
                      </p>
                    ) : (
                      <div className="space-y-4">
                        {categories.map((category) => (
                          <div key={category._id} className="p-4 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm">
                            {editCategory && editCategory._id === category._id ? (
                              <form onSubmit={(e) => handleSaveEdit(e, category._id)} className="flex items-center space-x-4">
                                <input
                                  type="text"
                                  value={editCategory.name}
                                  onChange={(e) => setEditCategory({ ...editCategory, name: e.target.value })}
                                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
                                  placeholder="Enter new category name"
                                />
                                <button
                                  type="submit"
                                  className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                  disabled={!editCategory.name.trim()}
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  onClick={cancelEditing}
                                  className="px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300"
                                >
                                  Cancel
                                </button>
                              </form>
                            ) : (
                              <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                  <span className="font-semibold text-lg text-gray-800 dark:text-gray-200">
                                    {category.name}
                                  </span>
                                  <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                                    ({category.eventCount} events)
                                  </span>
                                </div>
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => startEditing(category)}
                                    className="px-3 py-1 bg-primary-500 text-white rounded-md hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleDeleteCategory(category._id)}
                                    className="px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500"
                                    disabled={category.eventCount > 0}
                                    title={category.eventCount > 0 ? "Cannot delete category with associated events" : "Delete category"}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}